import axiosInstance from "../../axios/axiosInstance";
import {
  BibleVersion,
  BibleBook,
  BibleChapter,
  BibleVerse,
  BibleVerseSearchParams,
  BibleImportData,
  Language,
  BibleVersionNew,
  Occasion,
  OccasionVerse,
  CreateOccasionVerseDto,
} from "./types";
export interface ApiResponse<T> {
  data: T;
  status: "ok" | "error";
  error?: { message: string; code: string };
  pagination?: any;
}

export class BibleApi {
  private baseUrl = "/api/bible-management";

  // Bible Versions
  async getAllVersions(): Promise<BibleVersion[]> {
    const response = await axiosInstance.get<ApiResponse<BibleVersion[]>>(`${this.baseUrl}/bibles`);
    return response.data?.data || response.data || [];
  }

  async getVersionById(id: number): Promise<BibleVersion> {
    const response = await axiosInstance.get<ApiResponse<BibleVersion>>(`${this.baseUrl}/versions/${id}`);
    return response.data?.data || response.data;
  }

  async importVersion(data: BibleImportData): Promise<ApiResponse<BibleVersion>> {
    const formData = new FormData();
    formData.append("name", data.version_name);
    formData.append("language_code", data.language_code);
    formData.append("update", data.update ? "true" : "false");
    // formData.append("languageCode", data.languageCode);
    if (data.description) formData.append("description", data.description);
    if (data.abbreviation) formData.append("abbreviation", data.abbreviation);
    if (data.file) formData.append("file", data.file);
    if (data.structuredData) formData.append("structuredData", data.structuredData);

    const response = await axiosInstance.post<ApiResponse<any>>(`${this.baseUrl}/import/sqlite`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data.data;
  }

  // Languages
  async getLanguages(): Promise<Language[]> {
    const response = await axiosInstance.get<ApiResponse<Language[]>>(`${this.baseUrl}/languages`);
    return response.data?.data || response.data || [];
  }

  // Bible Books
  async getAllBooks(): Promise<BibleBook[]> {
    const response = await axiosInstance.get<ApiResponse<{ books: BibleBook[] }>>(`${this.baseUrl}/bibles/1/books`);
    console.log(response.data?.data?.books)
    return response.data?.data?.books || [] as any;
  }

  async getBookById(id: number): Promise<BibleBook> {
    const response = await axiosInstance.get<ApiResponse<BibleBook>>(`${this.baseUrl}/books/${id}`);
    return response.data?.data || response.data;
  }

  // Bible Chapters
  async getChaptersByBook(bookId: number): Promise<BibleChapter[]> {
    const response = await axiosInstance.get<ApiResponse<{ chapters: BibleChapter[] }>>(`${this.baseUrl}/bibles/1/books/${bookId}/chapters`);
    console.log(response.data?.data?.chapters)
    return response.data?.data?.chapters || response.data || [];
  }

  // Bible Verses
  async getVerses(params: {
    versionIds?: number[];
    bookId?: number;
    chapterNumber?: number;
    verseNumber?: number;
  }): Promise<{ [id: number]: BibleVersionNew }> {
    const queryParams = new URLSearchParams();
    // if (params.versionIds && params.versionIds.length > 0) {
    //   params.versionIds.forEach((id) => queryParams.append("version_ids", id.toString()));
    // }
    if (params.versionIds) queryParams.append("version_ids", params.versionIds.join(","));
    if (params.bookId) queryParams.append("book_index", params.bookId.toString());
    if (params.chapterNumber) queryParams.append("chapter_index", params.chapterNumber.toString());
    if (params.verseNumber) queryParams.append("verse_index", params.verseNumber.toString());

    const response = await axiosInstance.get<ApiResponse<{ [id: number]: BibleVersionNew }>>(`${this.baseUrl}/verses/side-by-side?${queryParams.toString()}`);
    return response.data?.data || response.data || {};
  }

  async searchVerses(params: BibleVerseSearchParams): Promise<BibleVerse[]> {
    const queryParams = new URLSearchParams();
    if (params.searchText) queryParams.append("searchText", params.searchText);
    if (params.language) queryParams.append("language", params.language);
    if (params.versionIds && params.versionIds.length > 0) {
      params.versionIds.forEach((id) => queryParams.append("versionIds", id.toString()));
    }
    if (params.bookId) queryParams.append("bookId", params.bookId.toString());
    if (params.chapterNumber) queryParams.append("chapterNumber", params.chapterNumber.toString());
    if (params.verseNumber) queryParams.append("verseNumber", params.verseNumber.toString());

    const response = await axiosInstance.get<ApiResponse<BibleVerse[]>>(`${this.baseUrl}/verses/search?${queryParams.toString()}`);
    return response.data?.data || response.data || [];
  }

  async getVerseByReference(
    versionId: number,
    bookId: number,
    chapterNumber: number,
    verseNumber: number
  ): Promise<BibleVerse> {
    const response = await axiosInstance.get<ApiResponse<BibleVerse>>(
      `${this.baseUrl}/verses/${versionId}/${bookId}/${chapterNumber}/${verseNumber}`
    );
    return response.data?.data || response.data;
  }

  async getVersesForComparison(
    versionIds: number[],
    bookId: number,
    chapterNumber: number,
    verseNumber?: number
  ): Promise<BibleVerse[]> {
    const queryParams = new URLSearchParams();
    versionIds.forEach((id) => queryParams.append("version_ids", id.toString()));
    queryParams.append("book_index", bookId.toString());
    queryParams.append("chapter_index", chapterNumber.toString());
    if (verseNumber) queryParams.append("verse_index", verseNumber.toString());

    const response = await axiosInstance.get<ApiResponse<BibleVerse[]>>(`${this.baseUrl}/verses/compare?${queryParams.toString()}`);
    return response.data?.data || response.data || [];
  }

  // Occasions
  async getAllOccasions(): Promise<Occasion[]> {
    const response = await axiosInstance.get<ApiResponse<Occasion[]>>(`${this.baseUrl}/occasions`);
    return response.data?.data || [];
  }

  // Occasion Verses
  async getAllOccasionVerses(): Promise<OccasionVerse[]> {
    const queryParams = new URLSearchParams();
    queryParams.append("language_code", "en");

    const response = await axiosInstance.get<ApiResponse<OccasionVerse[]>>(`${this.baseUrl}/verses/occasion?${queryParams.toString()}`);
    return response.data?.data || [];
  }

  async getAllOccasionVerseByOccassion(occasion_id: string, language_code: string): Promise<OccasionVerse[]> {
    const queryParams = new URLSearchParams();
    queryParams.append("language_code", language_code);
    queryParams.append("occasion_id", occasion_id);

    const response = await axiosInstance.get<ApiResponse<OccasionVerse[]>>(`${this.baseUrl}/verses/occasion?${queryParams.toString()}`);
    return response.data?.data || [];
  }

  async getOccasionVersesByOccasion(occasionId: number): Promise<OccasionVerse[]> {
    const response = await axiosInstance.get<ApiResponse<OccasionVerse[]>>(`${this.baseUrl}/occasion-verses/occasion/${occasionId}`);
    return response.data?.data || response.data || [];
  }

  async createOccasionVerse(data: CreateOccasionVerseDto): Promise<OccasionVerse> {
    const response = await axiosInstance.post<ApiResponse<OccasionVerse>>(`${this.baseUrl}/verses/mark-occasion`, data);
    return response.data?.data || response.data;
  }

  async updateOccasionVerse(id: number, data: Partial<CreateOccasionVerseDto>): Promise<OccasionVerse> {
    const response = await axiosInstance.put<ApiResponse<OccasionVerse>>(`${this.baseUrl}/occasion-verses/${id}`, data);
    return response.data?.data || response.data;
  }

  async deleteOccasionVerse(id: number): Promise<void> {
    await axiosInstance.delete(`${this.baseUrl}/occasion-verses/${id}`);
  }

  async markVerseAsDefault(bible_occasion_verse_id: number, occasion_id: number): Promise<void> {
    await axiosInstance.post(`${this.baseUrl}/verses/mark-default`, {
      bible_occasion_verse_id,
      occasion_id,
    });
  }
}

export const bibleApi = new BibleApi();

